import { useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import { Box, Radio, Skeleton, Typography } from "@mui/material";
import { alpha, useTheme } from "@mui/material/styles";
import { useQuery } from "@tanstack/react-query";
import { ThumbsDown, ThumbsUp } from "lucide-react";
import { toast } from "react-toastify";

import { Button } from "../../../components/Button";
import { CustomTextArea } from "../../../components/CustomTextArea";
import { CustomTextField } from "../../../components/CustomTextField";
import { Dialog, DialogContent } from "../../../components/Dialog";
import {
  clientifyErrorMessage,
  closeClientifyDeal,
  listClientifyLostReasons,
} from "../../../services/clientifyService";

const today = () => new Date().toISOString().slice(0, 10);

/**
 * Clientify's "¿Quieres marcar esta oportunidad como perdida?" dialog, plus
 * its won counterpart (closing date and final amount).
 */
export const CloseDealModal = ({ deal, result, onCancel, onClosed }) => {
  const theme = useTheme();
  const open = Boolean(deal && result);
  const isLost = result === "lost";
  const [reasonSearch, setReasonSearch] = useState("");
  const [reasonId, setReasonId] = useState(null);
  const [comment, setComment] = useState("");
  const [closedDate, setClosedDate] = useState(today());
  const [amount, setAmount] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    setReasonSearch("");
    setReasonId(null);
    setComment("");
    setClosedDate(today());
    setAmount(deal?.amount ?? "");
  }, [open, deal]);

  const { data: reasons = [], isLoading: reasonsLoading } = useQuery({
    queryKey: ["clientify-lost-reasons"],
    queryFn: listClientifyLostReasons,
    enabled: open && isLost,
    staleTime: 10 * 60 * 1000,
  });

  const filteredReasons = useMemo(() => {
    const term = reasonSearch.trim().toLowerCase();
    return term ? reasons.filter((reason) => reason.name.toLowerCase().includes(term)) : reasons;
  }, [reasons, reasonSearch]);

  const accent = isLost ? theme.palette.error.main : theme.palette.success.main;
  const canConfirm = isLost ? Boolean(reasonId) : Boolean(closedDate);

  const handleConfirm = async () => {
    setSaving(true);
    try {
      const payload = { result, closed_date: closedDate, comment: comment.trim() };
      if (isLost) payload.lost_reason = reasonId;
      if (!isLost && amount !== "") payload.amount = amount;
      const saved = await closeClientifyDeal(deal.id, payload);
      toast.success(isLost ? "Oportunidad marcada como perdida" : "Oportunidad marcada como ganada");
      onClosed(saved, deal);
    } catch (error) {
      toast.error(clientifyErrorMessage(error, "No se pudo cerrar la oportunidad"));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog
      open={open}
      onOpenChange={() => !saving && onCancel()}
      maxWidth="xs"
      fullWidth
      sx={{ zIndex: 1400 }}
    >
      <DialogContent sx={{ p: 3 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 2 }}>
          <Box
            sx={{
              width: 56,
              height: 56,
              flexShrink: 0,
              borderRadius: "10px",
              backgroundColor: alpha(accent, 0.1),
              color: accent,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            {isLost ? <ThumbsDown size={26} /> : <ThumbsUp size={26} />}
          </Box>
          <Typography sx={{ fontSize: "1.125rem", fontWeight: 600, lineHeight: 1.3 }}>
            {isLost
              ? "¿Quieres marcar esta oportunidad como perdida?"
              : "¿Quieres marcar esta oportunidad como ganada?"}
          </Typography>
        </Box>

        {deal?.name && (
          <Typography sx={{ fontSize: "0.875rem", fontWeight: 600, mb: 1.5 }}>
            {deal.name}
          </Typography>
        )}

        {isLost ? (
          <>
            <Typography
              sx={{ fontSize: "0.8125rem", fontStyle: "italic", color: "text.secondary", mb: 1 }}
            >
              Por favor, selecciona una razón para marcar esta oportunidad como perdida.
            </Typography>
            <CustomTextField
              isSearch
              placeholder="Buscar"
              value={reasonSearch}
              onChange={(e) => setReasonSearch(e.target.value)}
            />
            <Box sx={{ mt: 1, maxHeight: 200, overflowY: "auto" }}>
              {reasonsLoading
                ? [1, 2, 3].map((i) => <Skeleton key={i} height={32} />)
                : filteredReasons.map((reason) => (
                  <Box
                    key={reason.id}
                    onClick={() => setReasonId(reason.id)}
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      cursor: "pointer",
                      borderRadius: "6px",
                      "&:hover": { backgroundColor: theme.palette.action.hover },
                    }}
                  >
                    <Radio size="small" checked={reasonId === reason.id} />
                    <Typography sx={{ fontSize: "0.875rem" }}>{reason.name}</Typography>
                  </Box>
                ))}
              {!reasonsLoading && filteredReasons.length === 0 && (
                <Typography sx={{ fontSize: "0.8125rem", color: "text.secondary", py: 1 }}>
                  No hay razones que coincidan
                </Typography>
              )}
            </Box>
          </>
        ) : (
          <Box sx={{ display: "flex", gap: 1.5 }}>
            <Box sx={{ flex: 1 }}>
              <Typography sx={{ fontSize: "0.8125rem", mb: 0.5 }}>Fecha de cierre</Typography>
              <CustomTextField
                type="date"
                value={closedDate}
                onChange={(e) => setClosedDate(e.target.value)}
              />
            </Box>
            <Box sx={{ flex: 1 }}>
              <Typography sx={{ fontSize: "0.8125rem", mb: 0.5 }}>Importe final</Typography>
              <CustomTextField
                type="number"
                min="0"
                step="0.01"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
              />
            </Box>
          </Box>
        )}

        <Box sx={{ mt: 1.5 }}>
          <CustomTextArea
            rows={2}
            placeholder="Comentario (opcional)"
            value={comment}
            onChange={(e) => setComment(e.target.value)}
          />
        </Box>

        <Box
          sx={{
            display: "flex",
            justifyContent: "flex-end",
            gap: 1.5,
            mt: 2,
            pt: 2,
            borderTop: `1px solid ${theme.palette.divider}`,
          }}
        >
          <Button variant="ghost" onClick={onCancel} disabled={saving}>
            Cancelar
          </Button>
          <Button onClick={handleConfirm} disabled={!canConfirm || saving} sx={{ minWidth: 120 }}>
            {saving ? "Guardando..." : "Confirmar"}
          </Button>
        </Box>
      </DialogContent>
    </Dialog>
  );
};

CloseDealModal.propTypes = {
  deal: PropTypes.object,
  result: PropTypes.oneOf(["won", "lost", null]),
  onCancel: PropTypes.func.isRequired,
  onClosed: PropTypes.func.isRequired,
};
